import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { useNavigate } from 'react-router-dom';
import { useMutation as useApolloMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { UPDATE_CAR } from '../../../services/carService/queries';
import {
  IUpdateCarMutation,
  IUpdateCarMutationVariables,
} from '../../../services/carService/__generated__/queries.generated';
import { useUpdateEditCarStoreHook } from '../../../hooks/useUpdateEditCarStoreHook';
import { carActions } from '../../../../store/carStore';
import NumericInput from '../../../components/NumericInput';

const Container = styled.div`
  ${tw`w-full p-6 flex flex-col items-center justify-center`}
`;

// Inner wrapper to control form width on medium+ screens
const FormWrapper = styled.div`
  ${tw`w-full mx-auto`}
  max-width: 500px;
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold mb-8`}
`;

const Section = styled.div`
  ${tw`mb-6 w-full`}
`;

const Label = styled.label`
  ${tw`block text-text-secondary font-medium mb-2`}
`;

const StyledInput = styled.input`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  box-sizing: border-box;
`;

const StyledTextArea = styled.textarea`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  box-sizing: border-box;
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-center w-full mt-6`}
`;

const NextButton = styled.button<{ isActivated: boolean }>`
  ${tw`w-full text-white py-3 rounded-lg font-semibold transition-colors shadow-xs`}
  ${({ isActivated }) =>
    isActivated
      ? css`
          ${tw`bg-black`}

          &:hover {
            ${tw`bg-black-hover`}
          }
        `
      : tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`}
`;

const RequiredIndicator = styled.span`
  ${tw`text-red-500 ml-1`}
`;

export default function AddDetailsForNotCar() {
  const navigate = useNavigate();
  const { editingCar, successfullUpdatedEditingCarId } = useUpdateEditCarStoreHook();
  const [displayName, setDisplayName] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [description, setDescription] = useState('');

  const [updateCar, { loading: updating }] = useApolloMutation<
    IUpdateCarMutation,
    IUpdateCarMutationVariables
  >(UPDATE_CAR);

  useEffect(() => {
    if (editingCar) {
      setDisplayName(editingCar.displayName || '');
      setMake(editingCar.make || '');
      setModel(editingCar.model || '');
      setYear(editingCar.year ? editingCar.year.toString() : '');
      setDescription(editingCar.description || '');
    }
  }, [editingCar]);

  const handleSubmit = async () => {
    if (!editingCar?.id) {
      toast.error('Item not found. Please restart the listing flow.');
      navigate('/cars/host');
      return;
    }

    const derivedName = [make, model, year].filter(Boolean).join(' ').trim();
    const nameToSave = derivedName || editingCar.name || 'New Item';

    // displayName 必填：>=3 个字符
    const trimmedDisplayName = displayName.trim();
    if (trimmedDisplayName.length < 3) {
      toast.error('Display Name must be at least 3 characters long');
      return;
    }

    try {
      const { data } = await updateCar({
        variables: {
          id: editingCar.id,
          updateCarInput: {
            name: nameToSave,
            displayName: trimmedDisplayName,
            make,
            model,
            year: year ? Number(year) : null,
            description,
          },
        },
      });

      const updatedCar = data?.updateCar;
      if (updatedCar?.id) {
        carActions.startEditing(updatedCar.id, updatedCar);
      }

      navigate('/cars/host/add-price');
    } catch (error) {
      toast.error('Failed to save item details. Please try again.');
    }
  };

  if (!successfullUpdatedEditingCarId) {
    return <Container>Loading...</Container>;
  }

  const isActivated =
    displayName.trim().length >= 3 &&
    !!make.trim() &&
    !!model.trim() &&
    !!year.trim() &&
    !!description.trim();

  return (
    <Container>
      <FormWrapper>
        <Title>Item Details</Title>
        <Section>
          <Label>
            Display Name<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <StyledInput
            value={displayName}
            onChange={event => setDisplayName(event.target.value)}
            placeholder="How this item will be shown to guests"
          />
        </Section>
        <Section>
          <Label>
            Make<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <StyledInput
            value={make}
            onChange={event => setMake(event.target.value)}
            placeholder="Brand or manufacturer"
          />
        </Section>
        <Section>
          <Label>
            Model<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <StyledInput
            value={model}
            onChange={event => setModel(event.target.value)}
            placeholder="Model or variant"
          />
        </Section>
        <Section>
          <Label>
            Year<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <NumericInput
            value={year ? parseInt(year) : null}
            onValueChange={(value: number | null) => setYear(value?.toString() ?? '')}
            placeholder="e.g. 2025"
            minValue={1900}
            width="100%"
          />
        </Section>
        <Section>
          <Label>
            Description<RequiredIndicator>*</RequiredIndicator>
          </Label>
          <StyledTextArea
            rows={4}
            value={description}
            onChange={event => setDescription(event.target.value)}
            placeholder="Describe the item, key features, condition, etc."
          />
        </Section>
        <ButtonContainer>
          <NextButton
            isActivated={isActivated && !updating}
            disabled={!isActivated || updating}
            onClick={handleSubmit}
          >
            Next
          </NextButton>
        </ButtonContainer>
      </FormWrapper>
    </Container>
  );
}
